import { css } from '@emotion/react';
import {
  faCheck,
  faExpandArrows, faPlay, faStop, faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useMemo,
} from 'react';
import { ButtonStyle } from '~/_shared/baseComponents/buttons/button/button.types';
import { useDisclaimerSteps } from '~/_shared/components/disclaimer/disclaimerSteps';
import { DrawType } from '~/store/frontendState/mapTools/boundary/boundaryDraw/boundaryDraw.state';
import { DisclaimerContainer } from '../../../../_shared/components/disclaimer/disclaimer.container';
import { DisclaimerStepComponent } from '../../../../_shared/components/disclaimer/disclaimerStep.component';
import { MapConfirmComponent } from '../../../../_shared/mapConfirm/mapConfirm.component';
import { noop } from '../../../../_shared/utils/function.helpers';
import { useTranslation } from '../../../../_shared/utils/hooks';

type MoveMarkersSaveProps = Readonly<{
  isMoveMarkersModalVisible: boolean;
  isMoveMarkersActive: boolean;
  markersMoved: boolean;

  onConfirmClick: () => void;
  onClose: () => void;
  onResetClick: () => void;
  onStartClick: () => void;
}>;

const disclaimerStyle = css({
  marginLeft: 20,
  fontSize: '14px',
});

const mapConfirmStyle = css({
  minWidth: 267,
});

const buttonsWrapperStyle = ({ buttonsCount }: { buttonsCount: number }) => css({
  display: 'flex',
  justifyContent: buttonsCount <= 1 ? 'flex-end' : 'space-between',
});

export const MoveMarkersSaveComponent: FC<MoveMarkersSaveProps> = (props) => {
  const [t] = useTranslation();
  const disclaimerSteps = useDisclaimerSteps();

  const buttons = useMemo(() => {
    if (!props.isMoveMarkersActive) {
      return [{
        icon: faPlay,
        buttonStyle: ButtonStyle.Success,
        onClick: props.onStartClick,
        label: t('Start'),
      }];
    }
    else {
      if (!props.markersMoved) {
        return [{
          icon: faStop,
          buttonStyle: ButtonStyle.Danger,
          onClick: props.onResetClick,
          label: t('Stop'),
        }, {
          icon: faCheck,
          buttonStyle: ButtonStyle.Primary,
          onClick: noop,
          label: t('Save'),
          isDisabled: true,
        }];
      }
      else {
        return [{
          icon: faTrash,
          buttonStyle: ButtonStyle.Danger,
          onClick: props.onResetClick,
          label: t('Reset'),
        }, {
          icon: faCheck,
          buttonStyle: ButtonStyle.Primary,
          onClick: props.onConfirmClick,
          label: t('Save'),
        }];
      }
    }
  }, [props.isMoveMarkersActive, props.markersMoved, props.onConfirmClick,
    props.onResetClick, props.onStartClick, t]);

  return (
    <MapConfirmComponent
      isOpen={props.isMoveMarkersModalVisible}
      buttonsWrapperStyle={buttonsWrapperStyle({ buttonsCount: buttons.length })}
      buttons={buttons}
      dropdownOptions={[{
        name: t('Edit'),
        value: 'Drag',
        icon: faExpandArrows,
      }]}
      selectedOptionId={DrawType.Drag}
      onClose={props.onClose}
      heading={t('Move Markers')}
      css={mapConfirmStyle}
      selectionGuide={(
        <DisclaimerContainer
          css={disclaimerStyle}
          permanentConfirmSettings={{
            id: 'move-markers',
            text: t('Do not show again'),
          }}
        >
          <DisclaimerStepComponent
            step={1}
            isHighlighted={!props.isMoveMarkersActive}
          >
            {disclaimerSteps.Start}
          </DisclaimerStepComponent>

          <DisclaimerStepComponent
            step={2}
            isHighlighted={props.isMoveMarkersActive}
            maxWidth={400}
          >
            {t('You can now drag and drop one of your map markers to a new location on the map. It will remain in the new location permanently if you save the change.')}
          </DisclaimerStepComponent>
        </DisclaimerContainer>
      )}
    />
  );
};
